/**
 * PlanAppBar — sticky Kopfzeile der Wochenplan-Ansicht.
 *
 * Enthält (von oben nach unten): die AdminBar (nur im „Bearbeiten für"-Modus),
 * die Toolbar (Zurück zu den Gruppen, Titel, Einstellungen, Sprachumschalter),
 * die Wochennavigation und die mobile Tagesübersicht.
 *
 * Navigationsverhalten unverändert: Zurück → `/groups`, Einstellungen →
 * `/groups/:id/settings`.
 */

import { Link as RouterLink } from "react-router-dom";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import ArrowBack from "@mui/icons-material/ArrowBack";
import SettingsOutlined from "@mui/icons-material/SettingsOutlined";
import DeleteOutline from "@mui/icons-material/DeleteOutline";
import LanguageSwitcher from "../../components/LanguageSwitcher";
import { useI18n } from "../../i18n/I18nProvider";
import WeekNav from "./WeekNav";
import DayStrip from "./DayStrip";
import AdminBar from "./AdminBar";
import type { DayView } from "./types";

export default function PlanAppBar({
  groupId,
  isoWeek,
  days,
  displayedUserId,
  dayLabels,
  onPrevWeek,
  onNextWeek,
  editingForName,
  onEndEditing,
  canDelete,
  onDelete,
}: {
  groupId: string;
  isoWeek: string;
  days: DayView[];
  displayedUserId: string | undefined;
  dayLabels: string[];
  onPrevWeek: () => void;
  onNextWeek: () => void;
  /** Gesetzt → AdminBar „Du bearbeitest für <Name>" wird angezeigt. */
  editingForName: string | null;
  onEndEditing: () => void;
  /** Nur Admins mit vorhandenem Wochenplan: zeigt „Woche löschen". */
  canDelete?: boolean;
  onDelete?: () => void;
}) {
  const { t } = useI18n();

  return (
    <AppBar
      position="sticky"
      color="inherit"
      elevation={0}
      sx={{
        top: "env(safe-area-inset-top, 0px)",
        borderBottom: 1,
        borderColor: "divider",
        bgcolor: "background.paper",
        backgroundImage: "none",
      }}
    >
      {editingForName != null && (
        <AdminBar name={editingForName} onEnd={onEndEditing} />
      )}

      <Toolbar sx={{ gap: 0.5, px: 1.5 }} disableGutters>
        <IconButton
          component={RouterLink}
          to="/groups"
          aria-label={t("weeklyPlan.header.backToGroups")}
        >
          <ArrowBack />
        </IconButton>
        <Box flex={1} minWidth={0}>
          <Typography fontWeight={700} noWrap>
            {t("weeklyPlan.title")}
          </Typography>
        </Box>
        <IconButton
          component={RouterLink}
          to={`/groups/${groupId}/settings`}
          aria-label={t("weeklyPlan.header.settings")}
        >
          <SettingsOutlined />
        </IconButton>
        {canDelete && (
          <IconButton
            aria-label={t("weeklyPlan.delete.button")}
            onClick={onDelete}
          >
            <DeleteOutline />
          </IconButton>
        )}
        <LanguageSwitcher />
      </Toolbar>

      <WeekNav isoWeek={isoWeek} onPrev={onPrevWeek} onNext={onNextWeek} />

      <DayStrip
        days={days}
        displayedUserId={displayedUserId}
        dayLabels={dayLabels}
      />
    </AppBar>
  );
}
